import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ApolloProvider, useLazyQuery } from '@apollo/client';
import { Toaster } from 'react-hot-toast';
import apolloClient from './apolloClient';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { GET_LICENCIA_SISTEMA } from './graphql/licencia';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Habitaciones from './pages/Habitaciones';
import Reservas from './pages/Reservas';
import Hospedajes from './pages/Hospedajes';
import CheckIn from './pages/CheckIn';
import Huespedes from './pages/Huespedes';
import ServiciosPage from './pages/ServiciosPage';
import ProductosPage from './pages/ProductosPage';
import ClientesPage from './pages/ClientesPage';
import Reportes from './pages/Reportes';
import Configuracion from './pages/Configuracion';
import POS from './pages/POS';
import CajaPage from './pages/CajaPage';
import FactuBox from './pages/FactuBox';
import MiLicencia from './pages/MiLicencia';
import Impresoras from './pages/Impresoras';
import Housekeeping from './pages/Housekeeping';
import GradosMilitares from './pages/GradosMilitares';
import CuentasCobro from './pages/CuentasCobro';
import Activacion from './pages/Activacion';
import SelectorPropiedad from './pages/SelectorPropiedad';
import GestionPropiedades from './pages/GestionPropiedades';
import Proveedores from './pages/Proveedores';
import Gastos from './pages/Gastos';
import Compras from './pages/Compras';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import './App.css';

// URL del API de licencias
const LICENSE_API_URL = import.meta.env.VITE_LICENSE_API_URL || 'http://localhost:4003/api/v1';

// Sincronizar datos de licencia desde BD a localStorage
function sincronizarLicenciaConLocalStorage(licenciaDB) {
  if (!licenciaDB) return;

  if (licenciaDB.codigo_licencia) {
    localStorage.setItem('licencia_codigo', licenciaDB.codigo_licencia);
  }
  if (licenciaDB.tipo_licencia) {
    localStorage.setItem('licencia_tipo', licenciaDB.tipo_licencia);
  }
  if (licenciaDB.estado_licencia) {
    localStorage.setItem('licencia_estado', licenciaDB.estado_licencia);
  }
  if (licenciaDB.fecha_vencimiento) {
    localStorage.setItem('licencia_vencimiento', licenciaDB.fecha_vencimiento);
  }
  if (licenciaDB.fecha_activacion) {
    localStorage.setItem('licencia_fecha_activacion', licenciaDB.fecha_activacion);
  }
  if (licenciaDB.nit) {
    localStorage.setItem('comercio_nit', licenciaDB.nit);
  }
  if (licenciaDB.nombre_comercial) {
    localStorage.setItem('comercio_nombre', licenciaDB.nombre_comercial);
  }
  if (licenciaDB.razon_social) {
    localStorage.setItem('comercio_razon_social', licenciaDB.razon_social);
  }
  if (licenciaDB.ciudad) {
    localStorage.setItem('comercio_ciudad', licenciaDB.ciudad);
  }
  if (licenciaDB.modulos) {
    localStorage.setItem('licencia_modulos', JSON.stringify(licenciaDB.modulos));
  }
  if (licenciaDB.modulos_version) {
    localStorage.setItem('licencia_modulos_version', String(licenciaDB.modulos_version));
  }
}

// Verificar estado de licencia (offline - desde localStorage)
function verificarLicenciaOffline() {
  const estado = localStorage.getItem('licencia_estado');
  const codigo = localStorage.getItem('licencia_codigo');
  const fechaVencimiento = localStorage.getItem('licencia_vencimiento');
  const tipoLicencia = localStorage.getItem('licencia_tipo');

  if (!estado || !codigo || estado === 'sin_activar') {
    return { activa: false, estado: 'sin_activar' };
  }

  if (estado === 'suspendida') {
    return { activa: false, estado: 'suspendida' };
  }

  // Licencia permanente (compra), siempre activa
  if (tipoLicencia === 'compra') {
    return { activa: true, estado: 'activa', permanente: true };
  }

  // Verificar vencimiento
  if (fechaVencimiento) {
    const hoyStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
    const hoy = new Date(hoyStr + 'T00:00:00');
    const venc = new Date(fechaVencimiento);
    venc.setHours(0, 0, 0, 0);
    const diasRestantes = Math.ceil((venc - hoy) / (1000 * 60 * 60 * 24));

    // Periodo de gracia de 15 dias
    if (diasRestantes < -15) {
      return { activa: false, estado: 'vencida', diasRestantes };
    }
  }

  return { activa: true, estado: 'activa' };
}

// Verificar estado de licencia online (contra el servidor de licencias)
async function verificarLicenciaOnline() {
  const codigo = localStorage.getItem('licencia_codigo');
  const nit = localStorage.getItem('comercio_nit');
  const modulosVersionLocal = parseInt(localStorage.getItem('licencia_modulos_version') || '0');

  if (!codigo || !nit) {
    return null;
  }

  try {
    const response = await fetch(`${LICENSE_API_URL}/estado`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        codigo,
        nit,
        modulos_version: modulosVersionLocal
      })
    });

    if (!response.ok) {
      console.warn('Error verificando licencia online:', response.status);
      return null;
    }

    const data = await response.json();

    if (data.success) {
      // Actualizar modulos si hay cambios
      if (data.modulos_actualizados && data.modulos) {
        localStorage.setItem('licencia_modulos', JSON.stringify(data.modulos));
        localStorage.setItem('licencia_modulos_version', String(data.modulos_version || 0));
      }

      // Actualizar localStorage con el estado del servidor
      if (data.estado === 'suspendida') {
        localStorage.setItem('licencia_estado', 'suspendida');
        return { activa: false, estado: 'suspendida', mensaje: data.mensaje };
      } else if (data.estado === 'vencida' && !data.permitirAcceso) {
        localStorage.setItem('licencia_estado', 'vencida');
        return { activa: false, estado: 'vencida', mensaje: data.mensaje };
      } else if (data.permitirAcceso) {
        localStorage.setItem('licencia_estado', 'activa');
        if (data.fecha_vencimiento) {
          localStorage.setItem('licencia_vencimiento', data.fecha_vencimiento);
        }
        return { activa: true, estado: data.estado, mensaje: data.mensaje };
      }
    }

    return null;
  } catch (error) {
    console.warn('No se pudo verificar licencia online:', error.message);
    return null;
  }
}

// Componente interno con logica de verificacion de licencia
function AppContent() {
  const [licenciaVerificada, setLicenciaVerificada] = useState(false);
  const [licenciaActiva, setLicenciaActiva] = useState(false);

  // Query para obtener licencia desde la BD (fuente principal de verdad)
  const [obtenerLicenciaBD] = useLazyQuery(GET_LICENCIA_SISTEMA, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.licenciaSistema) {
        const licenciaDB = data.licenciaSistema;

        // Sincronizar BD con localStorage
        sincronizarLicenciaConLocalStorage(licenciaDB);

        // Usar el estado de la BD como fuente de verdad
        setLicenciaActiva(licenciaDB.activa);
        setLicenciaVerificada(true);

        // Si hay licencia activa, verificar online para detectar suspensiones remotas
        if (licenciaDB.activa) {
          verificarLicenciaOnline();
        }
      }
    },
    onError: (error) => {
      console.warn('Error consultando licencia desde BD:', error.message);
      // Si falla la BD, usar localStorage como fallback
      const resultadoOffline = verificarLicenciaOffline();
      setLicenciaActiva(resultadoOffline.activa);
      setLicenciaVerificada(true);
    }
  });

  // Verificar licencia al cargar
  useEffect(() => {
    const verificarLicencia = async () => {
      try {
        await obtenerLicenciaBD();
      } catch (error) {
        console.warn('Error en verificacion inicial:', error);
        const resultadoOffline = verificarLicenciaOffline();
        setLicenciaActiva(resultadoOffline.activa);
        setLicenciaVerificada(true);
      }
    };

    verificarLicencia();

    // Verificar cada 5 minutos para detectar cambios remotos
    const intervalo = setInterval(async () => {
      // Primero verificar online (tiene prioridad sobre BD local)
      const resultadoOnline = await verificarLicenciaOnline();
      if (resultadoOnline) {
        if (!resultadoOnline.activa) {
          setLicenciaActiva(false);
          return; // No consultar BD si online dice inactiva
        } else {
          setLicenciaActiva(true);
        }
      }

      // Si no hubo resultado online, re-consultar BD
      if (!resultadoOnline) {
        try {
          await obtenerLicenciaBD();
        } catch (e) {
          // Ignorar errores en polling
        }
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalo);
  }, [obtenerLicenciaBD]);

  // Callback cuando se activa la licencia
  const handleLicenciaActivada = () => {
    setLicenciaActiva(true);
    window.location.href = '/login';
  };

  // Mostrar loading mientras verifica
  if (!licenciaVerificada) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--hotel-background, #f8fafc)' }}>
        <div style={{ textAlign: 'center', color: 'var(--hotel-text, #1f2937)' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 500 }}>Verificando licencia...</div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Ruta publica de activacion de licencia */}
        <Route path="/activacion" element={<Activacion onActivado={handleLicenciaActivada} />} />

        {/* Login - redirige a activacion si no hay licencia */}
        <Route path="/login" element={
          licenciaActiva ? <Login /> : <Navigate to="/activacion" replace />
        } />

        {/* Rutas protegidas */}
        <Route path="/*" element={
          licenciaActiva ? (
            <ProtectedRoute />
          ) : (
            <Navigate to="/activacion" replace />
          )
        }>
          {/* Selector de propiedad - sin Layout ni PropertyGuard */}
          <Route path="seleccionar-propiedad" element={<SelectorPropiedad />} />

          {/* Rutas con Layout - requieren propiedad seleccionada */}
          <Route element={<PropertyGuard />}>
            <Route element={<Layout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="habitaciones" element={<Habitaciones />} />
              <Route path="reservas" element={<Reservas />} />
              <Route path="hospedajes" element={<Hospedajes />} />
              <Route path="hospedajes/checkin" element={<CheckIn />} />
              <Route path="huespedes" element={<Huespedes />} />
              <Route path="clientes" element={<ClientesPage />} />
              <Route path="servicios" element={<ServiciosPage />} />
              <Route path="productos" element={<ProductosPage />} />
              <Route path="pos" element={<POS />} />
              <Route path="caja" element={<CajaPage />} />
              <Route path="grados-militares" element={<GradosMilitares />} />
              <Route path="cuentas-cobro" element={<CuentasCobro />} />
              <Route path="gastos" element={<Gastos />} />
              <Route path="compras" element={<Compras />} />
              <Route path="proveedores" element={<Proveedores />} />
              <Route path="reportes" element={<Reportes />} />
              <Route path="housekeeping" element={<Housekeeping />} />
              <Route path="impresoras" element={<Impresoras />} />
              <Route path="mi-licencia" element={<MiLicencia />} />
              <Route
                path="configuracion"
                element={
                  <PrivateRoute modulo="configuracion">
                    <Configuracion />
                  </PrivateRoute>
                }
              />
              <Route
                path="gestion-propiedades"
                element={
                  <PrivateRoute allowedRoles={['superadmin']}>
                    <GestionPropiedades />
                  </PrivateRoute>
                }
              />
              <Route path="factubox" element={<FactuBox />} />
            </Route>
          </Route>
        </Route>

      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ApolloProvider client={apolloClient}>
        <NotificationProvider>
          <ThemedToaster />
          <AppContent />
        </NotificationProvider>
      </ApolloProvider>
    </ThemeProvider>
  );
}

// Componente Toaster que responde al tema
function ThemedToaster() {
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 3000,
        style: {
          background: 'var(--hotel-card-background)',
          color: 'var(--hotel-text)',
          border: '1px solid var(--hotel-border)',
        },
        success: {
          style: {
            background: '#10b981',
            color: '#ffffff',
          },
        },
        error: {
          style: {
            background: '#ef4444',
            color: '#ffffff',
          },
        },
      }}
    />
  );
}

// Componente para rutas protegidas por autenticacion
function ProtectedRoute() {
  const token = localStorage.getItem('token');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

// Componente que verifica que hay una propiedad seleccionada
function PropertyGuard() {
  const propiedadId = localStorage.getItem('propiedad_id');

  if (!propiedadId) {
    return <Navigate to="/seleccionar-propiedad" replace />;
  }

  return <Outlet />;
}

export default App;
